<div>
  <div class="products-header">
    <div>
      <h1>Products {{ team ? 'for Team ' + team.name : '' }}</h1>
      <div class="summary">{{ selectedProductCount }} Active Products</div>
    </div>

    <div class="actions">
      <button
        mat-button
        type="button"
        [disabled]="isLoading || isSaving || !hasProductSelectionChanges()"
        (click)="cancelProductChanges()"
      >
        Cancel
      </button>
      <button
        *ngIf="teamId"
        mat-button
        type="button"
        [disabled]="isLoading || isSaving || !hasTeamProductOverride()"
        (click)="clearTeamProductOverride()"
      >
        <span class="button-content">
          <mat-spinner *ngIf="isResetting" [diameter]="16"></mat-spinner>
          <span>{{ isResetting ? 'Clearing...' : 'Clear Team Override' }}</span>
        </span>
      </button>
      <button
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="isLoading || isSaving || !hasProductSelectionChanges()"
        (click)="saveProducts()"
      >
        <span class="button-content">
          <mat-spinner *ngIf="isSaving && !isResetting" [diameter]="16"></mat-spinner>
          <span>{{ isSaving && !isResetting ? 'Saving...' : 'Save' }}</span>
        </span>
      </button>
    </div>
  </div>

  <div class="error" *ngIf="saveError">{{ saveError }}</div>

  <div class="busy-status" *ngIf="busyStatusMessage" role="status" aria-live="polite">
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    <div>{{ busyStatusMessage }}</div>
  </div>

  <div class="source-team-helper" *ngIf="teamId && (sourceTeamOptions.length > 0 || isSourceTeamOptionsLoading || sourceTeamOptionsError)">
    <button
      type="button"
      class="source-team-helper-header"
      [attr.aria-expanded]="isSourceTeamProductHelperExpanded"
      (click)="toggleSourceTeamProductHelper()"
    >
      <span class="source-team-helper-title">Select products from existing team</span>
      <span class="source-team-helper-action">{{ isSourceTeamProductHelperExpanded ? 'Hide' : 'Show' }}</span>
    </button>
    <div class="source-team-helper-body" *ngIf="isSourceTeamProductHelperExpanded">
      <div class="source-team-helper-controls">
        <mat-form-field appearance="outline">
          <mat-label>Team product source</mat-label>
          <mat-select
            [(ngModel)]="selectedSourceTeamId"
            [disabled]="isSourceTeamOptionsLoading || isSourceTeamProductPreviewLoading || sourceTeamOptions.length === 0"
          >
            <mat-option *ngFor="let sourceTeam of sourceTeamOptions" [value]="sourceTeam.id">
              {{ sourceTeam.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <button
          mat-stroked-button
          type="button"
          color="primary"
          [disabled]="!selectedSourceTeamId || isSourceTeamProductPreviewLoading || isSourceTeamOptionsLoading"
          (click)="loadSourceTeamProductPreview()"
        >
          Preview Team Products
        </button>
        <mat-spinner *ngIf="isSourceTeamOptionsLoading || isSourceTeamProductPreviewLoading" [diameter]="24"></mat-spinner>
      </div>
      <div class="source-team-helper-message warning" *ngIf="sourceTeamOptionsError">{{ sourceTeamOptionsError }}</div>
      <div class="source-team-helper-message warning" *ngIf="sourceTeamProductPreviewError">{{ sourceTeamProductPreviewError }}</div>
      <div class="source-team-preview" *ngIf="sourceTeamProductPreviewProductIds.length > 0 || sourceTeamProductPreviewSkippedProductIds.length > 0">
        <div
          class="source-team-helper-message warning"
          *ngIf="sourceTeamProductPreviewProductIds.length === 0 && sourceTeamProductPreviewSkippedProductIds.length > 0"
        >
          No source products are available for this team. The current selection was not changed.
        </div>
        <p class="source-team-summary">
          {{ sourceTeamProductPreviewProductIds.length }} matched products from the selected team.
          {{ sourceTeamProductPreviewAddedProductIds.length }} would be added,
          {{ sourceTeamProductPreviewOverlappingProductIds.length }} are already selected, and
          {{ sourceTeamProductPreviewRemovedProductIds.length }} would be removed.
          {{ sourceTeamProductPreviewAllowanceEligibleProductIds.length }} copied products allow payment with Wallit allowance.
        </p>
        <div class="source-team-diff">
          <div>
            <h3>Would Add</h3>
            <p *ngIf="sourceTeamProductPreviewAddedProductIds.length === 0">No products</p>
            <ul *ngIf="sourceTeamProductPreviewAddedProductIds.length > 0">
              <li *ngFor="let productId of sourceTeamProductPreviewAddedProductIds">{{ getProductDisplay(productId) }}</li>
            </ul>
          </div>
          <div>
            <h3>Already Selected</h3>
            <p *ngIf="sourceTeamProductPreviewOverlappingProductIds.length === 0">No products</p>
            <ul *ngIf="sourceTeamProductPreviewOverlappingProductIds.length > 0">
              <li *ngFor="let productId of sourceTeamProductPreviewOverlappingProductIds">{{ getProductDisplay(productId) }}</li>
            </ul>
          </div>
          <div>
            <h3>Would Remove</h3>
            <p *ngIf="sourceTeamProductPreviewRemovedProductIds.length === 0">No products</p>
            <ul *ngIf="sourceTeamProductPreviewRemovedProductIds.length > 0">
              <li *ngFor="let productId of sourceTeamProductPreviewRemovedProductIds">{{ getProductDisplay(productId) }}</li>
            </ul>
          </div>
          <div *ngIf="sourceTeamProductPreviewSkippedProductIds.length > 0">
            <h3>Skipped</h3>
            <ul>
              <li *ngFor="let productId of sourceTeamProductPreviewSkippedProductIds">{{ productId }}</li>
            </ul>
          </div>
        </div>
        <button
          mat-raised-button
          type="button"
          color="accent"
          [disabled]="!canApplySourceTeamProductPreview()"
          (click)="applySourceTeamProductPreview()"
        >
          Apply To Selection
        </button>
        <p class="source-team-applied" *ngIf="sourceTeamProductPreviewApplied">
          Team products were applied to the current selection. Use Save to persist.
        </p>
      </div>
    </div>
  </div>

  <div class="section-shell">
  <div
    #productsSection
    class="section"
    [attr.aria-busy]="isLoading || isSaving"
    (scroll)="updateProductScrollState()"
  >
    <div class="placeholder" *ngIf="isLoading">
      <span>Loading products...</span>
    </div>

    <div #productList class="product-list" [class.product-list-busy]="isSaving" *ngIf="!isLoading && products.length > 0">
      <div class="product-context" *ngIf="teamId">
        <div class="override-status" *ngIf="!hasTeamProductOverride()">Using company-level product settings</div>
        <div class="helper-text">
          This list edits team-specific product overrides. Company-level products are set by Wallit; please reach out to request updates.
        </div>
      </div>
      <div class="bulk-controls">
        <mat-checkbox
          class="bulk-product-control"
          aria-label="Select all products"
          [checked]="areAllSelectableProductsSelected()"
          [indeterminate]="areSomeSelectableProductsSelected()"
          [disabled]="isProductBulkSelectionDisabled"
          (change)="toggleAllProductSelections($event.checked)"
        ></mat-checkbox>
        <mat-checkbox
          *ngIf="teamId"
          class="bulk-allowance-control"
          aria-label="Allow payment with Wallit allowance for all selected products"
          [checked]="areAllAllowanceTargetsEligible()"
          [indeterminate]="areSomeAllowanceTargetsEligible()"
          [disabled]="isAllowanceBulkSelectionDisabled"
          (change)="toggleAllAllowanceEligibility($event.checked)"
        >
          <span class="bulk-label-spacer" aria-hidden="true">Allows payment with Wallit allowance</span>
        </mat-checkbox>
      </div>
      <div class="item" *ngFor="let item of products; trackBy: trackByProductId">
        <div class="product-row">
          <mat-checkbox
            [checked]="item.selected"
            [disabled]="item.locked || isSaving"
            (change)="toggleProductSelection(item, $event.checked)"
          ></mat-checkbox>
          <div class="logo-slot">
            <img *ngIf="item.loadedImage" class="logo" [src]="item.loadedImage" alt="" />
          </div>
          <div class="itemright">
            <span class="header">{{ item.vendorName }}</span>
            <span class="description">{{ item.productName }}</span>
          </div>
          <div class="allowance-control" *ngIf="canManageAllowanceEligibility(item)">
            <mat-checkbox
              class="allowance-checkbox"
              [checked]="item.allowanceEligible"
              [disabled]="!canToggleAllowanceEligibility(item) || item.allowanceSaving || isSaving"
              (change)="toggleAllowanceEligibility(item, $event.checked)"
            >
              Allows payment with Wallit allowance
            </mat-checkbox>
            <mat-spinner *ngIf="item.allowanceSaving" [diameter]="18"></mat-spinner>
          </div>
          <span class="locked-label" *ngIf="item.locked">Always Active</span>
        </div>
      </div>
    </div>

    <div class="placeholder" *ngIf="!isLoading && products.length === 0">
      No products available.
    </div>
  </div>
  <div class="product-scroll-hint" *ngIf="showProductScrollHint" aria-hidden="true">
    <span>Scroll for more products</span>
    <mat-icon>keyboard_arrow_down</mat-icon>
  </div>
  </div>
</div>
